import React, { useState } from 'react';
import { FiX, FiCheckCircle, FiGift } from 'react-icons/fi';
import api from '../../utils/api';

const ResolveTicketModal = ({ ticketId, withCoupon = false, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    resolutionType: 'info_provided',
    description: '',
    // Coupon fields
    discountType: 'fixed',
    discountValue: 100,
    maxDiscountAmount: '',
    couponMessage: 'We apologize for the inconvenience. Here\'s a coupon for your next booking.'
  });
  const [resolving, setResolving] = useState(false);

  // Resolution types
  const resolutionTypes = [
    { value: 'refund', label: 'Refund Processed' },
    { value: 'coupon', label: 'Coupon Provided' },
    { value: 'vendor_action', label: 'Vendor Action Taken' },
    { value: 'info_provided', label: 'Information Provided' },
    { value: 'no_action', label: 'No Action Required' },
    { value: 'duplicate', label: 'Duplicate Ticket' },
    { value: 'callback_scheduled', label: 'Callback Scheduled' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      alert('Please enter a resolution description');
      return;
    }

    setResolving(true);
    try {
      let response;

      if (withCoupon) {
        response = await api.post(`/support/tickets/${ticketId}/resolve-with-coupon`, {
          discountType: formData.discountType,
          discountValue: Number(formData.discountValue),
          maxDiscountAmount: formData.maxDiscountAmount ? Number(formData.maxDiscountAmount) : null,
          message: formData.couponMessage
        });
      } else {
        response = await api.post(`/support/tickets/${ticketId}/resolve`, {
          resolutionType: formData.resolutionType,
          description: formData.description.trim()
        });
      }

      if (response.data.success) {
        if (withCoupon && response.data.data?.coupon) {
          alert(`Ticket resolved! Coupon code: ${response.data.data.coupon.code}`);
        } else {
          alert('Ticket resolved successfully!');
        }
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error resolving ticket:', error);
      alert(error.response?.data?.message || 'Failed to resolve ticket');
    } finally {
      setResolving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`flex justify-between items-center p-4 border-b border-gray-200 ${withCoupon ? 'bg-purple-50' : 'bg-green-50'}`}>
          <h3 className={`text-lg font-semibold ${withCoupon ? 'text-purple-800' : 'text-green-800'} flex items-center gap-2`}>
            {withCoupon ? <FiGift size={20} /> : <FiCheckCircle size={20} />}
            {withCoupon ? 'Resolve with Coupon' : 'Resolve Ticket'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          {withCoupon ? (
            <>
              {/* Coupon Configuration */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="discountType"
                        value="fixed"
                        checked={formData.discountType === 'fixed'}
                        onChange={(e) => handleChange('discountType', e.target.value)}
                        className="mr-2"
                      />
                      Fixed Amount (₹)
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="discountType"
                        value="percentage"
                        checked={formData.discountType === 'percentage'}
                        onChange={(e) => handleChange('discountType', e.target.value)}
                        className="mr-2"
                      />
                      Percentage (%)
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => handleChange('discountValue', e.target.value)}
                    min="1"
                    max={formData.discountType === 'percentage' ? 100 : 10000}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.discountType === 'fixed' ? 'Amount in Rupees' : 'Percentage off (max 100%)'}
                  </p>
                </div>

                {formData.discountType === 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Discount Amount (Optional)
                    </label>
                    <input
                      type="number"
                      value={formData.maxDiscountAmount}
                      onChange={(e) => handleChange('maxDiscountAmount', e.target.value)}
                      min="0"
                      placeholder="No limit"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum discount amount in Rupees
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message to User <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.couponMessage}
                    onChange={(e) => handleChange('couponMessage', e.target.value)}
                    placeholder="Message to send along with the coupon..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    required
                  />
                </div>

                {/* Preview */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-purple-800 mb-2">Coupon Preview:</p>
                  <p className="text-sm text-purple-700">
                    {formData.discountType === 'fixed'
                      ? `₹${formData.discountValue} off on next booking`
                      : `${formData.discountValue}% off${formData.maxDiscountAmount ? ` (up to ₹${formData.maxDiscountAmount})` : ''}`
                    }
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* General Resolution */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resolution Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.resolutionType}
                    onChange={(e) => handleChange('resolutionType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    {resolutionTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resolution Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe how the issue was resolved..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    required
                  />
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={resolving}
              className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                withCoupon ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {resolving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Resolving...
                </>
              ) : (
                <>
                  {withCoupon ? <FiGift size={16} /> : <FiCheckCircle size={16} />}
                  {withCoupon ? 'Resolve & Send Coupon' : 'Resolve Ticket'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResolveTicketModal;
